import React, { FC, ReactNode, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from 'antd-mobile';

import { useNavbar } from '../../../hooks/use-navbar';
import { useLivretRead } from '../../../hooks/use-livret-read';
import { faroLogger } from '../../../observability/faro-logger';

const SECTION_KEY = 'nettoyeurs-hp';

/* ============================================================ */
/* Sub-components                                                */
/* ============================================================ */

const SECTIONS: { id: string; num: number; title: string }[] = [
  { id: 'objet', num: 1, title: 'Objet' },
  { id: 'resp', num: 2, title: 'Responsabilités' },
  { id: 'epi', num: 3, title: 'EPI' },
  { id: 'controles', num: 4, title: 'Contrôles' },
  { id: 'thermique', num: 5, title: 'Nettoyeur thermique' },
  { id: 'electrique', num: 6, title: 'Nettoyeur électrique' },
  { id: 'ravitaillement', num: 7, title: 'Ravitaillement' },
  { id: 'entretien', num: 8, title: 'Entretien' },
  { id: 'accident', num: 9, title: 'Accident' },
  { id: 'majeurs', num: 10, title: 'Règles d’or' },
];

const COLOR = {
  navy: 'var(--navy)',
  teal: '#31DCC8',
  white: 'var(--text-primary)',
  muted: 'var(--text-muted)',
  warn: '#F59E0B',
  warnBg: 'rgba(245,158,11,0.10)',
  warnBorder: 'rgba(245,158,11,0.45)',
  danger: '#FF8A95',
  dangerStrong: '#E63946',
  dangerBg: 'rgba(230,57,70,0.10)',
  dangerBorder: 'rgba(230,57,70,0.45)',
  ok: '#10B981',
  okBg: 'rgba(16,185,129,0.10)',
  okBorder: 'rgba(16,185,129,0.4)',
};

const Section: FC<{ id: string; num: number; title: string; children: ReactNode }> = ({
  id,
  num,
  title,
  children,
}) => (
  <section
    id={id}
    style={{
      scrollMarginTop: 72,
      marginTop: 14,
      padding: '14px',
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 14,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
      }}
    >
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 10,
          background: 'rgba(49,220,200,0.16)',
          color: COLOR.teal,
          fontSize: 14,
          fontWeight: 900,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {num}
      </span>
      <h2
        style={{
          margin: 0,
          fontSize: 18.5,
          fontWeight: 800,
          color: 'var(--text-primary)',
          letterSpacing: '-0.005em',
        }}
      >
        {title}
      </h2>
    </div>
    <div style={{ color: COLOR.white, fontSize: 16, lineHeight: 1.55 }}>
      {children}
    </div>
  </section>
);

const SubTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <h3
    style={{
      margin: '14px 0 8px',
      fontSize: 14.5,
      fontWeight: 800,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: COLOR.teal,
    }}
  >
    {children}
  </h3>
);

const Forbidden: FC<{ title?: string; children: ReactNode }> = ({ title, children }) => (
  <div
    style={{
      margin: '10px 0',
      padding: '12px 14px',
      background: COLOR.dangerBg,
      border: `1px solid ${COLOR.dangerBorder}`,
      borderLeft: `4px solid ${COLOR.dangerStrong}`,
      borderRadius: 10,
      color: 'var(--text-primary)',
    }}
  >
    {title && (
      <div
        style={{
          fontSize: 14.5,
          fontWeight: 800,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: COLOR.danger,
          marginBottom: 6,
        }}
      >
        ⛔ {title}
      </div>
    )}
    <div style={{ fontSize: 15.5, lineHeight: 1.55 }}>{children}</div>
  </div>
);

const Caution: FC<{ title?: string; children: ReactNode }> = ({ title, children }) => (
  <div
    style={{
      margin: '10px 0',
      padding: '12px 14px',
      background: COLOR.warnBg,
      border: `1px solid ${COLOR.warnBorder}`,
      borderLeft: `4px solid ${COLOR.warn}`,
      borderRadius: 10,
      color: 'var(--text-primary)',
    }}
  >
    {title && (
      <div
        style={{
          fontSize: 14.5,
          fontWeight: 800,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: COLOR.warn,
          marginBottom: 6,
        }}
      >
        ⚠️ {title}
      </div>
    )}
    <div style={{ fontSize: 15.5, lineHeight: 1.55 }}>{children}</div>
  </div>
);

const Bullets: FC<{ items: ReactNode[] }> = ({ items }) => (
  <ul style={{ margin: '6px 0 8px', paddingLeft: 18 }}>
    {items.map((it, i) => (
      <li
        key={i}
        style={{
          margin: '4px 0',
          color: COLOR.white,
          fontSize: 16,
          lineHeight: 1.5,
        }}
      >
        {it}
      </li>
    ))}
  </ul>
);

const CheckList: FC<{ items: string[] }> = ({ items }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '8px 0' }}>
    {items.map((it, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '8px 10px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 8,
        }}
      >
        <span
          style={{
            width: 16,
            height: 16,
            borderRadius: 4,
            border: `1.5px solid ${COLOR.teal}`,
            background: 'transparent',
            flexShrink: 0,
            marginTop: 2,
          }}
        />
        <span style={{ fontSize: 15.5, color: COLOR.white }}>{it}</span>
      </div>
    ))}
  </div>
);

const TwoCols: FC<{
  okTitle: string;
  okItems: string[];
  koTitle: string;
  koItems: string[];
}> = ({ okTitle, okItems, koTitle, koItems }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 8,
      margin: '10px 0',
    }}
  >
    <div
      style={{
        padding: '10px 11px',
        background: COLOR.okBg,
        border: `1px solid ${COLOR.okBorder}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: COLOR.ok,
          marginBottom: 6,
        }}
      >
        ✓ {okTitle}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 15,
          color: COLOR.white,
          lineHeight: 1.45,
        }}
      >
        {okItems.map((it, i) => (
          <div key={i}>• {it}</div>
        ))}
      </div>
    </div>
    <div
      style={{
        padding: '10px 11px',
        background: COLOR.dangerBg,
        border: `1px solid ${COLOR.dangerBorder}`,
        borderRadius: 10,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: COLOR.danger,
          marginBottom: 6,
        }}
      >
        ✘ {koTitle}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          fontSize: 15,
          color: COLOR.white,
          lineHeight: 1.45,
        }}
      >
        {koItems.map((it, i) => (
          <div key={i}>• {it}</div>
        ))}
      </div>
    </div>
  </div>
);

const EpiList: FC<{ items: { icon: string; label: string }[] }> = ({ items }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: 8,
      margin: '8px 0',
    }}
  >
    {items.map((it, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'rgba(49,220,200,0.06)',
          border: '1px solid rgba(49,220,200,0.25)',
          borderRadius: 10,
        }}
      >
        <span style={{ fontSize: 22, flexShrink: 0 }}>{it.icon}</span>
        <span style={{ fontSize: 14.5, color: COLOR.white, lineHeight: 1.3 }}>
          {it.label}
        </span>
      </div>
    ))}
  </div>
);

/* ============================================================ */
/* Main page                                                     */
/* ============================================================ */

export const LivretNettoyeurs: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { isRead, readAt, markAsRead } = useLivretRead(SECTION_KEY);

  const handleConfirm = (): void => {
    markAsRead();
    faroLogger.info('livret_section_read_confirmed', {
      section: SECTION_KEY,
      title: 'Nettoyeurs HP',
    });
    navigate('/livret');
  };

  useEffect(() => {
    if (!navbar) {
      return;
    }
    navbar.setTitle('Nettoyeurs HP');
    navbar.setBackArrow(true);
    navbar.setRight();
    navbar.setOnBack(() => {
      if (isRead) {
        navigate('/livret');
        return;
      }
      Dialog.confirm({
        title: '⚠ Lecture non validée',
        content:
          'Tu n’as pas encore validé la lecture de cette section. ' +
          'Reste sur la page et tape sur "✓ J’ai lu" en bas pour valider.',
        confirmText: 'Sortir quand même',
        cancelText: 'Rester sur la page',
      }).then((confirmed) => {
        if (confirmed) {
          faroLogger.warn('livret_section_exit_without_read', {
            section: SECTION_KEY,
          });
          navigate('/livret');
        } else {
          faroLogger.info('livret_section_exit_cancelled', {
            section: SECTION_KEY,
          });
        }
      });
    });
    faroLogger.info('livret_nettoyeurs_opened');
  }, [navbar, navigate, isRead]);

  const scrollTo = (id: string): void => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div ref={scrollRef} style={{ padding: '0 14px 28px', maxWidth: 800, margin: '0 auto' }}>
      {/* En-tete */}
      <header
        style={{
          padding: '16px 16px 14px',
          margin: '12px -14px 0',
          background:
            'linear-gradient(135deg, rgba(230,57,70,0.14), rgba(245,158,11,0.06))',
          borderTop: `3px solid ${COLOR.dangerStrong}`,
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: COLOR.danger,
            marginBottom: 4,
          }}
        >
          🛡 Procédure de sécurité
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 21,
            fontWeight: 900,
            color: 'var(--text-primary)',
            letterSpacing: '-0.015em',
            lineHeight: 1.25,
          }}
        >
          Utilisation, entretien et ravitaillement des nettoyeurs haute pression
        </h1>
        <div
          style={{
            fontSize: 14.5,
            color: 'var(--text-secondary)',
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          <b style={{ color: 'var(--text-primary)' }}>Matériel</b> : nettoyeurs HP thermiques et électriques
          <br />
          <b style={{ color: 'var(--text-primary)' }}>Personnel</b> : opérateurs, chefs d&rsquo;équipe, superviseurs
        </div>
      </header>

      {/* Sommaire collant */}
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: COLOR.navy,
          margin: '0 -14px',
          padding: '10px 14px',
          borderBottom: '1px solid var(--border-subtle)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div style={{ display: 'flex', gap: 6 }}>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollTo(s.id)}
              style={{
                appearance: 'none',
                background: 'var(--bg-card)',
                border: '1px solid rgba(49,220,200,0.25)',
                borderRadius: 100,
                padding: '6px 12px',
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <span style={{ color: COLOR.teal, fontWeight: 900 }}>{s.num}.</span>{' '}
              {s.title}
            </button>
          ))}
        </div>
      </nav>

      {/* ===== 1. OBJET ===== */}
      <Section id="objet" num={1} title="Objet">
        <p style={{ margin: '0 0 10px' }}>
          Définir les règles de sécurité relatives à l&rsquo;utilisation, au ravitaillement,
          à l&rsquo;entretien et au stockage des nettoyeurs haute pression afin de prévenir
          les risques :
        </p>
        <Bullets
          items={[
            'blessures par projection',
            'brûlures',
            'incendie',
            'électrocution',
            'glissades',
            'troubles musculosquelettiques',
            'dommages matériels',
          ]}
        />
      </Section>

      {/* ===== 2. RESPONSABILITES ===== */}
      <Section id="resp" num={2} title="Responsabilités">
        <SubTitle>Opérateur</SubTitle>
        <Bullets
          items={[
            'Vérifier l’état du matériel avant utilisation.',
            'Respecter les consignes de sécurité.',
            'Signaler toute anomalie.',
            'Réaliser les contrôles de premier niveau.',
          ]}
        />
        <SubTitle>Responsable d&rsquo;équipe</SubTitle>
        <Bullets
          items={[
            'Vérifier les habilitations et formations.',
            'Contrôler l’application de la procédure.',
            'Organiser les maintenances périodiques.',
          ]}
        />
      </Section>

      {/* ===== 3. EPI ===== */}
      <Section id="epi" num={3} title="Équipements de protection individuelle (EPI)">
        <SubTitle>Obligatoires pour tous les nettoyeurs</SubTitle>
        <EpiList
          items={[
            { icon: '🥾', label: 'Chaussures de sécurité antidérapantes' },
            { icon: '🧤', label: 'Gants protection mécanique' },
            { icon: '🥽', label: 'Lunettes ou écran facial' },
            { icon: '👖', label: 'Vêtements de travail couvrants' },
          ]}
        />
        <SubTitle>Selon l&rsquo;environnement</SubTitle>
        <EpiList
          items={[
            { icon: '🎧', label: 'Protection auditive (> 80 dB)' },
            { icon: '🧥', label: 'Veste imperméable' },
          ]}
        />
        <SubTitle>Pour le ravitaillement (modèles thermiques)</SubTitle>
        <EpiList
          items={[
            { icon: '🧤', label: 'Gants résistants aux hydrocarbures' },
            { icon: '🥽', label: 'Lunettes contre projections' },
          ]}
        />
      </Section>

      {/* ===== 4. CONTROLES ===== */}
      <Section id="controles" num={4} title="Contrôles avant utilisation">
        <SubTitle>Général</SubTitle>
        <CheckList
          items={[
            'Absence de fuite.',
            'Flexible en bon état.',
            'Lance en bon état.',
            'Poignée fonctionnelle.',
            'Raccords serrés.',
            'Carters de protection présents.',
          ]}
        />
        <SubTitle>Thermique</SubTitle>
        <CheckList
          items={[
            'Niveau d’huile moteur.',
            'Niveau carburant.',
            'État du bouchon de réservoir.',
            'Absence de fuite d’essence.',
          ]}
        />
        <SubTitle>Électrique</SubTitle>
        <CheckList
          items={[
            'Câble d’alimentation intact.',
            'Prise non endommagée.',
            'Rallonge adaptée.',
            'Dispositif différentiel fonctionnel.',
          ]}
        />
        <Caution>
          Toute anomalie entraîne la <b>mise hors service immédiate</b> de l&rsquo;équipement.
        </Caution>
      </Section>

      {/* ===== 5. NETTOYEUR THERMIQUE ===== */}
      <Section id="thermique" num={5} title="Utilisation d'un nettoyeur thermique">
        <Forbidden title="Interdit de faire fonctionner un nettoyeur thermique dans un véhicule ou un espace de chargement">
          Il est <b>strictement interdit</b> de faire fonctionner un nettoyeur HP thermique
          lorsqu&rsquo;il est maintenu à l&rsquo;intérieur d&rsquo;un véhicule utilitaire,
          d&rsquo;un camion, d&rsquo;une remorque fermée ou de tout autre espace de chargement.
          Le nettoyeur doit être <b>systématiquement descendu</b> du véhicule et installé sur
          une surface stable et dégagée avant sa mise en service. Éloigner toute source
          inflammable, vérifier la ventilation de la zone et la présence d&rsquo;eau.
        </Forbidden>

        <SubTitle>Risques associés</SubTitle>
        <Bullets
          items={[
            <>
              <b>🔥 Incendie</b> — échauffement moteur/échappement, carburant ou chiffons
              souillés à proximité, vapeurs inflammables en espace confiné.
            </>,
            <>
              <b>💥 Explosion</b> — vapeurs d&rsquo;essence enflammées au contact d&rsquo;une
              source chaude ou d&rsquo;une étincelle.
            </>,
            <>
              <b>☠️ Intoxication</b> — accumulation de gaz d&rsquo;échappement (monoxyde de
              carbone, incolore et inodore) pouvant provoquer malaise, perte de
              connaissance ou décès.
            </>,
            <>
              <b>🔥 Brûlure</b> — contact accidentel avec parties chaudes en espace
              restreint.
            </>,
            <>
              <b>🔧 Dégradation matériel</b> — surchauffe, mauvaise ventilation,
              détérioration prématurée.
            </>,
          ]}
        />

        <SubTitle>Pendant l&rsquo;utilisation</SubTitle>
        <TwoCols
          okTitle="Maintenir"
          okItems={[
            'Une posture stable.',
            'Une distance de sécurité avec les tiers.',
          ]}
          koTitle="Interdictions"
          koItems={[
            'Ne jamais diriger le jet vers une personne.',
            'Ne jamais laisser fonctionner sans surveillance.',
            'Ne jamais utiliser dans un local fermé.',
            'Ne jamais intervenir sur le moteur en fonctionnement.',
          ]}
        />

        <SubTitle>Après utilisation</SubTitle>
        <Bullets
          items={[
            'Couper le moteur.',
            'Fermer l’alimentation carburant si applicable.',
            'Dépressuriser le circuit.',
            'Laisser refroidir avant déplacement ou rangement.',
          ]}
        />
      </Section>

      {/* ===== 6. NETTOYEUR ELECTRIQUE ===== */}
      <Section id="electrique" num={6} title="Utilisation d'un nettoyeur électrique">
        <Forbidden title="Interdit de faire fonctionner un nettoyeur électrique dans le véhicule">
          Il est interdit de faire fonctionner un nettoyeur HP électrique à l&rsquo;intérieur
          du véhicule utilitaire, du camion ou de la remorque. Le nettoyeur doit être
          <b> sorti du véhicule</b> avant toute utilisation et installé sur une surface
          stable et adaptée.
        </Forbidden>

        <SubTitle>Risques associés</SubTitle>
        <Bullets
          items={[
            <>
              <b>⚡ Risque électrique</b> — eau à proximité des câbles/prises, isolement
              dégradé, électrisation ou court-circuit.
            </>,
            <>
              <b>👟 Chute & manutention</b> — encombrement, trébuchement.
            </>,
            <>
              <b>🚐 Détérioration véhicule</b> — projection d&rsquo;eau sur parois,
              équipements embarqués ou installations électriques.
            </>,
            <>
              <b>💧 Humidité</b> — accumulation d&rsquo;eau, surfaces glissantes, corrosion.
            </>,
            <>
              <b>🧍 Ergonomie</b> — posture contrainte, accès difficile aux commandes
              d&rsquo;arrêt d&rsquo;urgence.
            </>,
          ]}
        />

        <SubTitle>Avant démarrage</SubTitle>
        <Bullets
          items={[
            'Vérifier la conformité de l’alimentation électrique.',
            <>
              Utiliser une prise protégée par <b>différentiel 30 mA</b>.
            </>,
            'Maintenir les connexions hors des flaques d’eau.',
          ]}
        />

        <SubTitle>Pendant l&rsquo;utilisation</SubTitle>
        <Forbidden title="Interdictions">
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Tirer sur le câble pour déplacer l&rsquo;appareil.</li>
            <li>Utiliser un câble détérioré.</li>
            <li>Travailler avec des connexions électriques immergées.</li>
          </ul>
        </Forbidden>

        <SubTitle>Après utilisation</SubTitle>
        <Bullets
          items={[
            'Arrêter l’appareil.',
            'Débrancher la prise.',
            'Enrouler correctement le câble.',
            'Stocker dans un endroit sec.',
          ]}
        />
      </Section>

      {/* ===== 7. RAVITAILLEMENT ===== */}
      <Section id="ravitaillement" num={7} title="Ravitaillement des nettoyeurs thermiques">
        <Caution title="Opération à risque d&rsquo;incendie">
          Doit être réalisée <b>uniquement par une personne formée</b>.
        </Caution>

        <SubTitle>Conditions obligatoires</SubTitle>
        <TwoCols
          okTitle="Le moteur doit être"
          okItems={[
            'Arrêté.',
            'Complètement refroidi.',
            'Positionné sur surface stable.',
          ]}
          koTitle="Interdit si"
          koItems={[
            'Moteur chaud.',
            'Moteur en marche.',
            'Près d’une flamme ou étincelle.',
            'Présence de fumeurs.',
          ]}
        />

        <SubTitle>Mode opératoire</SubTitle>
        <ol style={{ margin: '6px 0 8px', paddingLeft: 20 }}>
          {[
            'Arrêter le moteur.',
            'Attendre le refroidissement complet (10 à 30 minutes selon usage).',
            'Positionner un extincteur à proximité.',
            'Mettre les EPI requis.',
            'Ouvrir le réservoir lentement.',
            'Utiliser un entonnoir adapté.',
            'Remplir sans dépasser le niveau maximum.',
            'Essuyer immédiatement toute coulure.',
            'Refermer soigneusement le bouchon.',
            'Vérifier l’absence de fuite.',
            'Éloigner le bidon de carburant avant redémarrage.',
          ].map((it, i) => (
            <li
              key={i}
              style={{
                margin: '4px 0',
                color: COLOR.white,
                fontSize: 14.5,
                lineHeight: 1.5,
              }}
            >
              {it}
            </li>
          ))}
        </ol>

        <SubTitle>En cas de déversement</SubTitle>
        <Forbidden>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Stopper immédiatement l&rsquo;opération.</li>
            <li>Éliminer les sources d&rsquo;ignition.</li>
            <li>Absorber le carburant avec un produit adapté.</li>
            <li>Ne pas démarrer le moteur avant nettoyage complet.</li>
          </ul>
        </Forbidden>
      </Section>

      {/* ===== 8. ENTRETIEN ===== */}
      <Section id="entretien" num={8} title="Entretien">
        <SubTitle>Quotidien</SubTitle>
        <Bullets
          items={[
            'Nettoyage général.',
            'Vérification des flexibles.',
            'Contrôle des raccords.',
            'Contrôle des niveaux.',
          ]}
        />
        <SubTitle>Périodique (selon fabricant)</SubTitle>
        <Bullets
          items={[
            'Vidanges.',
            'Remplacement des filtres.',
            'Contrôle des joints.',
            'Contrôle des dispositifs de sécurité.',
            'Vérification électrique (modèles électriques).',
          ]}
        />
        <SubTitle>Traçabilité</SubTitle>
        <Bullets
          items={[
            'Numéro d’identification.',
            'Registre de maintenance.',
            'Historique des réparations.',
          ]}
        />
      </Section>

      {/* ===== 9. ACCIDENT ===== */}
      <Section id="accident" num={9} title="Conduite à tenir en cas d'accident">
        <div
          style={{
            padding: '12px 14px',
            background: 'rgba(245,158,11,0.10)',
            border: `1px solid ${COLOR.warnBorder}`,
            borderLeft: `4px solid ${COLOR.warn}`,
            borderRadius: 10,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 800,
              color: COLOR.warn,
              marginBottom: 6,
            }}
          >
            🤕 Brûlure
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 15.5, lineHeight: 1.55 }}>
            <li>Éloigner la victime du danger.</li>
            <li>
              Refroidir sous eau tempérée pendant <b>au moins 15 minutes</b>.
            </li>
            <li>Alerter les secours.</li>
          </ol>
        </div>
        <div
          style={{
            padding: '12px 14px',
            background: COLOR.dangerBg,
            border: `1px solid ${COLOR.dangerBorder}`,
            borderLeft: `4px solid ${COLOR.dangerStrong}`,
            borderRadius: 10,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 800,
              color: COLOR.danger,
              marginBottom: 6,
            }}
          >
            🔥 Départ de feu
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 15.5, lineHeight: 1.55 }}>
            <li>Couper le moteur ou l&rsquo;alimentation électrique.</li>
            <li>
              Utiliser un extincteur adapté <b>uniquement</b> si le feu est maîtrisable
              sans danger.
            </li>
            <li>Évacuer la zone si nécessaire.</li>
            <li>Appeler les secours.</li>
          </ol>
        </div>
        <div
          style={{
            padding: '12px 14px',
            background: 'rgba(59,130,246,0.10)',
            border: '1px solid rgba(59,130,246,0.4)',
            borderLeft: '4px solid #3B82F6',
            borderRadius: 10,
          }}
        >
          <div
            style={{
              fontSize: 14.5,
              fontWeight: 800,
              color: '#7AB3F5',
              marginBottom: 6,
            }}
          >
            ⚡ Électrisation
          </div>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: 15.5, lineHeight: 1.55 }}>
            <li>
              Couper l&rsquo;alimentation électrique <b>avant toute intervention</b>.
            </li>
            <li>
              Ne jamais toucher directement une victime encore en contact avec une source
              électrique.
            </li>
            <li>Alerter les secours.</li>
          </ol>
        </div>
      </Section>

      {/* ===== 10. REGLES D'OR ===== */}
      <Section id="majeurs" num={10} title="Points de sécurité majeurs">
        <SubTitle>Règles d&rsquo;or</SubTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            'Contrôler le matériel avant chaque utilisation.',
            'Porter les EPI requis.',
            'Ne jamais pointer le jet vers une personne.',
            'Ne jamais ravitailler un moteur chaud.',
            'Nettoyer immédiatement toute fuite de carburant.',
            'Signaler immédiatement toute anomalie.',
            'Maintenir le matériel conformément aux prescriptions du fabricant.',
          ].map((it, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                background: COLOR.okBg,
                border: `1px solid ${COLOR.okBorder}`,
                borderRadius: 10,
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: COLOR.ok,
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  fontWeight: 900,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                ✓
              </span>
              <span style={{ fontSize: 14.5, color: 'var(--text-primary)', lineHeight: 1.5 }}>{it}</span>
            </div>
          ))}
        </div>

        <Caution>
          Ces règles sont particulièrement importantes au regard de l&rsquo;accident récent
          survenu lors d&rsquo;une opération de ravitaillement d&rsquo;un nettoyeur
          thermique. Le retour d&rsquo;expérience doit conduire à une{' '}
          <b>vigilance renforcée sur le refroidissement complet</b> du moteur avant toute
          manipulation de carburant.
        </Caution>
      </Section>

      {/* Validation de lecture */}
      <div
        style={{
          margin: '20px 0 12px',
          padding: '14px',
          background: isRead
            ? 'rgba(16,185,129,0.10)'
            : 'rgba(49,220,200,0.06)',
          border: isRead
            ? '1px solid rgba(16,185,129,0.4)'
            : '1px solid rgba(49,220,200,0.4)',
          borderRadius: 14,
        }}
      >
        {isRead && readAt && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#10B981',
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ✓ Lu le{' '}
            {new Date(readAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}{' '}
            à{' '}
            {new Date(readAt).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        )}
        <button
          type="button"
          onClick={handleConfirm}
          style={{
            appearance: 'none',
            background: isRead
              ? 'var(--bg-card)'
              : 'linear-gradient(135deg, #31DCC8, #0FBDAD)',
            border: isRead ? '1px solid var(--border-strong)' : 'none',
            borderRadius: 12,
            padding: '14px 16px',
            color: isRead ? 'var(--text-primary)' : '#0B1628',
            fontFamily: 'inherit',
            fontSize: 16,
            fontWeight: 800,
            cursor: 'pointer',
            width: '100%',
            boxShadow: isRead ? 'none' : '0 6px 20px rgba(49,220,200,0.32)',
            letterSpacing: '-0.005em',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {isRead ? '↩ Retour au sommaire' : '✓ J’ai lu — retour au sommaire'}
        </button>
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: 8,
          padding: '12px 14px',
          textAlign: 'center',
          color: 'var(--text-faint)',
          fontSize: 10.5,
        }}
      >
        En cas de doute, demande à ton responsable d&rsquo;équipe.
      </div>
    </div>
  );
};
